import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { addEntities } from "@ngneat/elf-entities";
import { Action, ActionType, CreateAssetDispatchAction, CreateDispatchAction } from "../state/action.model";
import { ActionsStore } from "../state/actions.store";
import { LastRidersUpdate } from "../state/location-state.store";
import { LocationStateService } from "../state/location-state.service";
import { LocationStatisticsService } from "./location-statistics.service";

export const DISPATCH_UNDO_WINDOW_MS = 5000;

export interface DispatchUndoToast {
	riders: number;
	unitName?: string;
}

type HeldDispatch = {
	action: Action<CreateDispatchAction | CreateAssetDispatchAction>;
	riders: number;
	unitId?: string;
	previousUnitCount?: number;
	previousLastOccupiedSeats?: number;
	previousLastRidersUpdate?: LastRidersUpdate;
};

@Injectable({ providedIn: "root" })
export class DispatchUndoService {
	constructor(
		private actionsStore: ActionsStore,
		private locationStateService: LocationStateService,
		private locationStatistics: LocationStatisticsService,
	) {}

	private held: HeldDispatch | null = null;
	private releaseTimer: ReturnType<typeof setTimeout> | null = null;

	private readonly toastSubject = new BehaviorSubject<DispatchUndoToast | null>(null);
	readonly toast$ = this.toastSubject.asObservable();

	arm = async (
		action: Action<CreateDispatchAction | CreateAssetDispatchAction>,
		options: {
			riders: number;
			unitId?: string;
			unitName?: string;
			previousUnitCount?: number;
			previousLastOccupiedSeats?: number;
			previousLastRidersUpdate?: LastRidersUpdate;
		},
	) => {
		this.commitHeld();

		await this.locationStatistics.refresh();
		this.locationStatistics.beginHoldOptimistic(options.riders);

		this.held = {
			action,
			riders: options.riders,
			unitId: options.unitId,
			previousUnitCount: options.previousUnitCount,
			previousLastOccupiedSeats: options.previousLastOccupiedSeats,
			previousLastRidersUpdate: options.previousLastRidersUpdate,
		};

		this.toastSubject.next({ riders: options.riders, unitName: options.unitName });
		this.releaseTimer = setTimeout(() => this.commitHeld(), DISPATCH_UNDO_WINDOW_MS);
	};

	undo = () => {
		if (this.held == null) {
			return;
		}

		const held = this.held;
		this.clearTimer();
		this.held = null;
		this.toastSubject.next(null);
		this.locationStatistics.clearHoldOptimistic();
		this.locationStateService.setDispatchCooldownUntil(undefined);

		if (held.action.actionType === ActionType.CREATE_ASSET_DISPATCH) {
			this.locationStateService.revertHeldAssetDispatch(held.riders, {
				previousLastOccupiedSeats: held.previousLastOccupiedSeats,
				previousLastRidersUpdate: held.previousLastRidersUpdate,
				unitId: held.unitId,
				previousUnitCount: held.previousUnitCount ?? null,
			});
			return;
		}

		this.locationStateService.revertDispatchesAndRiders(held.riders, 1, held.previousLastRidersUpdate);
	};

	commitHeld = () => {
		if (this.held == null) {
			return;
		}

		const action = this.held.action;
		this.clearTimer();
		this.held = null;
		this.toastSubject.next(null);
		this.locationStatistics.foldHoldOptimisticIntoServer();
		this.actionsStore.store.update(addEntities(action));
	};

	private clearTimer = () => {
		if (this.releaseTimer != null) {
			clearTimeout(this.releaseTimer);
			this.releaseTimer = null;
		}
	};
}
